import React from 'react';
import { Settings, MessageSquare, Search, Radio } from 'lucide-react';
import { features, testimonial } from '../data/mockData';

const iconMap = {
  Settings: Settings,
  MessageSquare: MessageSquare,
  Search: Search,
  Radio: Radio,
};

const colorMap = {
  1: { bg: 'bg-blue-50', text: 'text-blue-500', border: 'border-blue-100' },
  2: { bg: 'bg-pink-50', text: 'text-pink-500', border: 'border-pink-100' },
  3: { bg: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-100' },
  4: { bg: 'bg-green-50', text: 'text-green-500', border: 'border-green-100' },
};

const FeaturesSection = () => {
  return (
    <section id="features" className="py-16 sm:py-24 px-3 sm:px-4 bg-gray-50 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <p className="text-[#25D366] font-medium mb-3 sm:mb-4 uppercase tracking-wide text-xs sm:text-sm">Features</p>
          <h2 className="text-xl sm:text-3xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            Elevating your <span className="italic">Customer Support</span>
          </h2>
          <h3 className="text-xl sm:text-3xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Experience to <span className="italic">new heights</span>
          </h3>
          <p className="text-gray-600 text-sm sm:text-lg max-w-2xl mx-auto px-2">
            Creating a Seamless and Personalized Support Journey for All.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-10 sm:mb-16">
          {features.map((feature) => {
            const IconComponent = iconMap[feature.icon];
            const colors = colorMap[feature.id];
            return (
              <div 
                key={feature.id} 
                className={`${colors.bg} rounded-xl sm:rounded-2xl p-5 sm:p-8 border ${colors.border} hover:shadow-lg transition-shadow`}
              >
                <div className={`w-10 sm:w-12 h-10 sm:h-12 bg-white rounded-lg sm:rounded-xl flex items-center justify-center mb-4 sm:mb-6 shadow-sm`}>
                  <IconComponent className={`w-5 sm:w-6 h-5 sm:h-6 ${colors.text}`} />
                </div>
                <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2">
                  {feature.title} {feature.subtitle}
                </h4>
                <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Testimonial Quote */}
        <div className="relative bg-gradient-to-br from-[#25D366]/10 via-white to-blue-50 rounded-xl sm:rounded-3xl p-4 sm:p-6 md:p-14 shadow-xl border border-[#25D366]/20 overflow-hidden">
          <div className="absolute top-0 left-0 w-20 sm:w-32 h-20 sm:h-32 bg-[#25D366]/10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-24 sm:w-40 h-24 sm:h-40 bg-blue-100/50 rounded-full translate-x-1/4 translate-y-1/4"></div>
          <div className="hidden md:block absolute top-6 right-8 text-[#25D366]/20 text-8xl font-serif">"</div>
          <div className="relative">
            <p className="text-base sm:text-xl md:text-3xl text-gray-800 leading-relaxed mb-4 sm:mb-6 md:mb-10 font-medium italic">
              "{testimonial.quote}"
            </p>
            <div className="flex items-center gap-2 sm:gap-3 md:gap-5">
              <div className="relative flex-shrink-0">
                <img
                  src={testimonial.avatar}
                  alt={testimonial.author}
                  className="w-12 sm:w-16 h-12 sm:h-16 rounded-full object-cover ring-2 sm:ring-4 ring-[#25D366]/30 ring-offset-1 sm:ring-offset-2"
                />
                <div className="absolute -bottom-1 -right-1 w-5 sm:w-6 h-5 sm:h-6 bg-[#25D366] rounded-full flex items-center justify-center">
                  <svg className="w-2.5 sm:w-3 h-2.5 sm:h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="min-w-0">
                <p className="font-bold text-gray-900 text-sm sm:text-lg truncate">{testimonial.author}</p>
                <p className="text-[#25D366] font-medium text-xs sm:text-base truncate">{testimonial.role}, {testimonial.company}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
